/**
 * encodeWav — encode an array of mono Float32 PCM frames to a 16-bit PCM
 * WAV blob. Sample rate is the rate the AudioWorklet captured at, which
 * (for our context) is forced to 16 kHz.
 *
 * 16-bit PCM @ 16 kHz mono is exactly what Whisper expects natively, so
 * the server doesn't need ffmpeg in the hot path.
 *
 * One 15-second utterance ≈ 480 KB. Fine on 4G.
 */

export function encodeWav(frames: Float32Array[], sampleRate: number): Blob {
    const totalSamples = frames.reduce((acc, f) => acc + f.length, 0);
    const merged = new Float32Array(totalSamples);
    let offset = 0;
    for (const f of frames) {
        merged.set(f, offset);
        offset += f.length;
    }

    // Float32 → Int16 PCM
    const pcm = new Int16Array(merged.length);
    for (let i = 0; i < merged.length; i++) {
        const s = Math.max(-1, Math.min(1, merged[i]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    const headerSize = 44;
    const buffer = new ArrayBuffer(headerSize + pcm.byteLength);
    const view = new DataView(buffer);

    // RIFF header
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + pcm.byteLength, true);
    writeString(view, 8, 'WAVE');

    // fmt chunk (PCM 16-bit mono)
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // chunk size
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); // byte rate
    view.setUint16(32, 2, true); // block align
    view.setUint16(34, 16, true); // bits per sample

    // data chunk
    writeString(view, 36, 'data');
    view.setUint32(40, pcm.byteLength, true);

    // Copy samples
    new Int16Array(buffer, headerSize).set(pcm);

    return new Blob([buffer], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, str: string): void {
    for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
    }
}

/** Compute total recorded duration in milliseconds. */
export function framesDurationMs(frames: Float32Array[], sampleRate: number): number {
    const samples = frames.reduce((acc, f) => acc + f.length, 0);
    return Math.round((samples / sampleRate) * 1000);
}

/** Compute peak amplitude (0..1) from a single frame, for visualizer. */
export function peakAmplitude(frame: Float32Array): number {
    let peak = 0;
    for (let i = 0; i < frame.length; i++) {
        const a = Math.abs(frame[i]);
        if (a > peak) peak = a;
    }
    return peak;
}
